import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import tenant from '../../config/tenant';

export function getAuthKeyFromLocalStorage() {
  const primary = localStorage.getItem('AuthKey') || localStorage.getItem('auth-key') || localStorage.getItem('auth_key');
  if (primary && primary.trim()) {
    console.debug('[auth] token found under primary key');
    return primary.trim();
  }

  const FALLBACK_KEYS = ['token', 'authKey', 'authToken', 'auth', 'user', 'authData', 'Authkey'];
  for (const key of FALLBACK_KEYS) {
    const raw = localStorage.getItem(key);
    if (!raw) continue;

    if (!raw.startsWith('{') && !raw.startsWith('[') && raw.trim().length > 8) {
      console.debug(`[auth] token found under fallback key "${key}" (plain string)`);
      return raw.trim();
    }

    try {
      const parsed = JSON.parse(raw);
      const t = parsed?.Token || parsed?.token || parsed?.auth_key || parsed?.authKey || parsed?.AuthKey;
      if (t) {
        console.debug(`[auth] token found under fallback key "${key}" (parsed object)`);
        return t;
      }
    } catch {
    }
  }

  console.warn('[auth] No auth token found in localStorage.');
  return null;
}

async function empFetch(url, token) {
  console.debug(`[empFetch] GET ${url}`);
  const headers = {
    'Content-Type': 'application/json',
  };
  const key = token || getAuthKeyFromLocalStorage();
  if (key) {
    headers['auth-key'] = key;
  }

  const res = await fetch(url, {
    method: 'GET',
    headers,
  });

  console.debug(`[empFetch] Response status: ${res.status}`);

  if (!res.ok) {
    const text = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(text || `HTTP error ${res.status}`);
  }

  const json = await res.json();
  console.debug('[empFetch] Response JSON:', json);
  return json;
}

function normaliseEmployeeResult(res) {
  if (!res) return null;

  if (Array.isArray(res)) {
    return res.length > 0 ? res[0] : null;
  }

  if (res.ResultSet && Array.isArray(res.ResultSet)) {
    return res.ResultSet.length > 0 ? res.ResultSet[0] : null;
  }

  if (res.Result && Array.isArray(res.Result)) {
    return res.Result.length > 0 ? res.Result[0] : null;
  }

  if (typeof res === 'object') {
    return res;
  }

  return null;
}

export const fetchAllEmployees = createAsyncThunk(
  'employees/fetchAll',
  async (_, { rejectWithValue }) => {
    const token = getAuthKeyFromLocalStorage();
    try {
      const data = await empFetch(`${tenant.api.baseUrl}/Emp/GetEmployees`, token);

      let list = [];
      if (Array.isArray(data)) {
        list = data;
      } else if (data && Array.isArray(data.ResultSet)) {
        list = data.ResultSet;
      } else if (data && Array.isArray(data.Result)) {
        list = data.Result;
      } else if (data && data.StatusCode === 200 && Array.isArray(data.Data)) {
        list = data.Data;
      } else if (data && data.Message) {
        return rejectWithValue(data.Message);
      }

      console.info(`[employees] Loaded ${list.length} employees`);
      return list;
    } catch (err) {
      console.error('[employees] fetchAllEmployees error:', err);
      return rejectWithValue(err.message || 'Network error. Check your connection.');
    }
  }
);

export const fetchEmployeeByServiceNo = createAsyncThunk(
  'employees/fetchByServiceNo',
  async (empIdOrServiceNo, { rejectWithValue }) => {
    if (!empIdOrServiceNo) return rejectWithValue('Employee ID or Service number is required.');

    const token = getAuthKeyFromLocalStorage();

    try {
      const url = `${tenant.api.baseUrl}/Emp/GetEmployeebyID?p_hedEmployeeId=${encodeURIComponent(empIdOrServiceNo)}`;
      const data = await empFetch(url, token);

      const employee = normaliseEmployeeResult(data);

      if (!employee) {
        return rejectWithValue(`No record found for ID/Service number: ${empIdOrServiceNo}`);
      }

      console.info(`[employees] Loaded employee detail:`, employee);
      return employee;

    } catch (err) {
      console.error('[employees] fetchEmployeeByServiceNo error:', err);
      return rejectWithValue(err.message || 'Network error. Check your connection.');
    }
  }
);

export const fetchEmployeeProfilePicUrl = createAsyncThunk(
  'employees/fetchProfilePicUrl',
  async (empId, { rejectWithValue }) => {
    if (!empId) return rejectWithValue('Employee ID is required.');
    const token = getAuthKeyFromLocalStorage();
    try {
      const url = `${tenant.api.baseUrl}/Emp/GetEmployeebyID?p_hedEmployeeId=${encodeURIComponent(empId)}`;
      const data = await empFetch(url, token);
      const employee = normaliseEmployeeResult(data);

      const docs =
        employee?.Documents ||
        data?.Documents ||
        data?.ResultSet?.[0]?.Documents ||
        data?.Result?.[0]?.Documents ||
        [];

      const pic = docs.find(
        (d) => String(d?.DocType || '').toLowerCase() === 'profilepic'
      );
      return pic?.URL || null;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load profile picture');
    }
  }
);

// ─── fetch all approved leaves ────────────────────────────────
export const fetchApprovedLeaves = createAsyncThunk(
  'employees/fetchApprovedLeaves',
  async (_, { rejectWithValue }) => {
    const token = getAuthKeyFromLocalStorage();
    try {
      const base = String(tenant.api.baseUrl || '').replace(/\/+$/, '');
      const url = `${base}/LeaveApprovels/GetAllRequestLeaveList?p_leaveStatus=APPROVED`;
      const data = await empFetch(url, token);

      let list = [];
      if (Array.isArray(data)) list = data;
      else if (data && Array.isArray(data.ResultSet)) list = data.ResultSet;
      else if (data && Array.isArray(data.Result)) list = data.Result;
      else if (data && data.StatusCode === 200 && Array.isArray(data.Data)) list = data.Data;
      else if (data && data.Message) return rejectWithValue(data.Message);

      console.info(`[employees] Loaded ${list.length} approved leaves`);
      return list;
    } catch (err) {
      console.error('[employees] fetchApprovedLeaves error:', err);
      return rejectWithValue(err.message || 'Failed to fetch approved leaves');
    }
  }
);

const initialState = {
  employees:        [],
  approvedLeaves:   [],     
  selectedEmployee: null,
  loading:          false,
  detailLoading:    false,
  leavesLoading:    false,       
  leavesError:      null,    
  error:            null,
  modalOpen:        false,
  searchQuery:      '',
};

const employeeSlice = createSlice({
  name: 'employees',
  initialState,

  reducers: {
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setEmployees(state, action) {
      state.employees = action.payload;
    },
    setSelectedEmployee(state, action) {
      state.selectedEmployee = action.payload;
      state.modalOpen        = action.payload !== null;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    setModalOpen(state, action) {
      state.modalOpen = action.payload;
      if (!action.payload) state.selectedEmployee = null;
    },
    setSearchQuery(state, action) {
      state.searchQuery = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchAllEmployees.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchAllEmployees.fulfilled, (state, action) => {
        state.loading   = false;
        state.employees = action.payload;
      })
      .addCase(fetchAllEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload ?? 'Failed to load employees';
      });

    builder
      .addCase(fetchEmployeeByServiceNo.pending, (state) => {
        state.detailLoading = true;
        state.error         = null;
      })
      .addCase(fetchEmployeeByServiceNo.fulfilled, (state, action) => {
        state.detailLoading    = false;
        state.selectedEmployee = action.payload;
        state.modalOpen        = true;
      })
      .addCase(fetchEmployeeByServiceNo.rejected, (state, action) => {
        state.detailLoading = false;
        state.error         = action.payload ?? 'Failed to load employee details';
      });

    // ─── NEW ───
    builder
      .addCase(fetchApprovedLeaves.pending, (state) => {
        state.leavesLoading = true;
        state.leavesError   = null;
      })
      .addCase(fetchApprovedLeaves.fulfilled, (state, action) => {
        state.leavesLoading  = false;
        state.approvedLeaves = action.payload;
      })
      .addCase(fetchApprovedLeaves.rejected, (state, action) => {
        state.leavesLoading = false;
        state.leavesError   = action.payload ?? 'Failed to load approved leaves';
      });
  },
});

export const {
  setLoading,
  setEmployees,
  setSelectedEmployee,
  setError,
  setModalOpen,
  setSearchQuery,
  clearError,
} = employeeSlice.actions;

// ─── selector ──────────────────────────────────────────────────
export const selectApprovedLeaves = (state) =>
  state.employees?.approvedLeaves || [];

export default employeeSlice.reducer;